// @ts-strict-ignore
import queryString from 'query-string';

import {handleJsonRequest} from 'common/report/queries/utils';
import {getReportUrl, isProduction} from 'common/report/util';
import {getLocation} from 'common/util/url';

import {TicketType, ZendeskTicketData, ZendeskUpload} from './types';

export const zendeskApi = {
  submitZendeskTicket,
  uploadZendeskFile,
};

const zendeskHost = isProduction()
  ? `https://mixpanelsupport.zendesk.com`
  : `https://mixpanelsupport1553889792.zendesk.com`;

function zendeskTicketFields(data: ZendeskTicketData, ticketType: TicketType) {
  let zendeskFieldId, zendeskTicketFormId, fields;
  const {id, serviceArr, supportPlan, supportPlanTier, totalArr} = data;
  if (isProduction()) {
    // To find these IDs, Look at the names of all the non-system form fields
    // fields marked as "end-user-editable" from the Zendesk ticket form URL above.
    // Then go to the ticket fields admin page and note the 'Field ID' value for
    // each of the field names
    //
    // Zendesk agent dashboard -> Admin -> Manage -> Ticket Field
    //
    // Zendesk production ticket fields URL:
    // https://mixpanelsupport.zendesk.com/agent/admin/ticket_fields
    zendeskFieldId = {
      BillingAccountId: 360021982332,
      FeatureGateName: 360021982252,
      FeatureFlags: 6425642934804,
      MtuEventPlanId: 360021982192,
      PeoplePlanId: 360021982212,
      RequestSubtype: 360022979492,
      RequestType: 360021982112,
      ServiceArr: 360021982172,
      SupportPlan: 360021981892,
      Tier: 360021982312,
      TotalArr: 360021982132,
      UserAgeInDays: 360021982272,
      UserId: 360021982292,
    };

    // The ID of the zendesk ticket form containing all the ticket fields
    // used by the support team for inbound support requests. To find the
    // ticket form id, go to the Zendesk ticket forms admin page:
    //
    // Zendesk agent dashboard -> Admin -> Manage -> Ticket Forms
    //
    // Zendesk ticket form URL:
    // https://mixpanelsupport.zendesk.com/agent/admin/ticket_forms/edit/360000425131
    // https://mixpanelsupport.zendesk.com/agent/admin/ticket_forms/edit/360000667732
    zendeskTicketFormId = ticketType === TicketType.SupportTicket ? 360000425131 : 360000667732;
  } else {
    // Zendesk sandbox ticket fields URL:
    // https://mixpanelsupport1553889792.zendesk.com/agent/admin/ticket_fields
    zendeskFieldId = {
      BillingAccountId: 360021027492,
      FeatureGateName: 360021084851,
      FeatureFlags: 6422004723860,
      MtuEventPlanId: 360021084391,
      PeoplePlanId: 360021084431,
      RequestSubtype: 360023272291,
      RequestType: 360019336991,
      ServiceArr: 360021027532,
      SupportPlan: 360019282972,
      Tier: 360021569172,
      TotalArr: 360021027512,
      UserAgeInDays: 360021027952,
      UserId: 360021027972,
    };
    // Zendesk sandbox ticket form URL:
    // https://mixpanelsupport1553889792.zendesk.com/agent/admin/ticket_forms/edit/360000364591
    zendeskTicketFormId = ticketType === TicketType.SupportTicket ? 360000364591 : 360000667732;
  }
  if (ticketType === TicketType.SupportTicket) {
    fields = {
      [zendeskFieldId.RequestSubtype]: data.requestSubtype,
      [zendeskFieldId.RequestType]: data.requestType,
      [zendeskFieldId.SupportPlan]: supportPlan,
      [zendeskFieldId.TotalArr]: totalArr,
      [zendeskFieldId.ServiceArr]: serviceArr,
      [zendeskFieldId.FeatureFlags]: data.features.join(`, `),
      [zendeskFieldId.MtuEventPlanId]: data.eventsPlanId,
      [zendeskFieldId.PeoplePlanId]: data.peoplePlanId,
      [zendeskFieldId.FeatureGateName]: data.featureGateName,
      [zendeskFieldId.UserAgeInDays]: data.userAgeInDays,
      [zendeskFieldId.BillingAccountId]: id,
      [zendeskFieldId.UserId]: data.userId,
      [zendeskFieldId.Tier]: supportPlanTier,
    };
  } else if (ticketType === TicketType.StartupTicket) {
    fields = {
      [zendeskFieldId.SupportPlan]: supportPlan,
      [zendeskFieldId.TotalArr]: totalArr,
      [zendeskFieldId.ServiceArr]: serviceArr,
      [zendeskFieldId.FeatureFlags]: data.features.join(`, `),
      [zendeskFieldId.MtuEventPlanId]: data.eventsPlanId,
      [zendeskFieldId.PeoplePlanId]: data.peoplePlanId,
      [zendeskFieldId.FeatureGateName]: data.featureGateName,
      [zendeskFieldId.UserAgeInDays]: data.userAgeInDays,
      [zendeskFieldId.BillingAccountId]: id,
      [zendeskFieldId.UserId]: data.userId,
      [zendeskFieldId.Tier]: supportPlanTier,
    };
  }
  return {
    zendeskTicketFormId,
    fields,
  };
}

/**
 * This is the request payload format used by the Zendesk Web Widget
 * as of Aug 2019 to create a Zendesk ticket via /api/v2/requests.json
 *
 * The `request.fields` format is undocumented in their REST API docs. The
 * docs wrongly list a `request.custom_fields` value that doesn't get used
 * by their API.
 *
 * Zendesk create ticket REST API docs:
 * https://developer.zendesk.com/rest_api/docs/support/requests#create-request
 * https://developer.zendesk.com/rest_api/docs/support/requests#json-format
 *
 * The value of 48 for via_id shows the ticket as submitted by Web Widget:
 * https://developer.zendesk.com/rest_api/docs/support/views#via-types
 */
function zendeskRequestData(data: ZendeskTicketData, ticketType: TicketType) {
  const {zendeskTicketFormId, fields} = zendeskTicketFields(data, ticketType);
  // Remove contact-us from the query string to prevent the support ticket modal from showing up
  const queryParams = queryString.parse(getLocation().search);
  delete queryParams[`contact-us`];
  const newSearch = queryString.stringify(queryParams);
  const submittedUrl = getLocation().href.replace(getLocation().search, newSearch && `?` + newSearch);

  let replayComment = ``;
  if (mixpanel.get_session_recording_properties?.()?.[`$mp_replay_id`]) {
    const replayId = mixpanel.get_session_recording_properties()[`$mp_replay_id`];
    const searchParams = new URLSearchParams();
    searchParams.set(`replay_id`, replayId);
    searchParams.set(`distinct_id`, mixpanel.get_distinct_id());
    const replayUrl = `${getLocation().origin}${getReportUrl(`profile`, {projectId: 3})}#${searchParams.toString()}`;
    replayComment = `\nSession Replay: ${replayUrl}`;
  }

  return {
    request: {
      subject: data.subject,
      tags: [`web_widget`],
      via_id: 48,
      comment: {
        body: `${data.comment}\n\n------------------\nSubmitted from: ${submittedUrl}${replayComment}`,
        uploads: data.uploads.map((upload) => upload.token),
      },
      requester: {
        name: data.userName,
        email: data.userEmail,
        locale_id: 1,
      },
      ticket_form_id: zendeskTicketFormId,
      fields,
    },
  };
}

/**
 * Zendesk create ticket API docs:
 * https://developer.zendesk.com/rest_api/docs/support/requests#create-request
 */
function submitZendeskTicket(data: ZendeskTicketData, ticketType: TicketType) {
  const path = `/api/v2/requests.json`;
  const requestData = zendeskRequestData(data, ticketType);
  return handleJsonRequest(
    fetch(`${zendeskHost}${path}`, {
      body: JSON.stringify(requestData),
      headers: {
        'Content-Type': `application/json`,
      },
      method: `POST`,
    }),
  );
}

/**
 * Zendesk upload attachment API docs:
 * https://developer.zendesk.com/rest_api/docs/support/attachments#upload-files
 *
 * As of Aug 2019, the API docs don't mention the `uploaded_data` form data
 * key. This function replicates the API call made by the Zendesk Web Widget.
 */
async function uploadZendeskFile(file: File): Promise<ZendeskUpload> {
  const path = `/api/v2/uploads.json`;
  const formData = new FormData();
  formData.append(`uploaded_data`, file);
  const {upload} = await handleJsonRequest(
    fetch(`${zendeskHost}${path}?filename=${file.name}&via_id=48`, {
      body: formData,
      method: `POST`,
    }),
  );
  return upload;
}
